<script setup lang="ts">
import Banner from "@/components/ui/Banner.vue";
import { useQuery } from "@tanstack/vue-query";
import BookABetWidget from "~/components/layout/BookABet/BookABetWidget.vue";
import SimpleViewCombinedView from "~/components/layout/Sport/SimpleViewCombinedView.vue";
import SportGameList from "~/components/layout/Sport/SportGameList.vue";
import SportSimpleViewActions from "~/components/layout/Sport/SportSimpleViewActions.vue";
import NewsBox from "~/components/layout/Sport/SportsBook/NewsBox/index.vue";
import SportsLayout from "~/components/layout/Sport/SportsLayout.vue";
import SportsBookDownLanding from "~/components/layout/SportsBookDownLanding.vue";
import RacingView from "~/components/layout/TrackRacing/RacingView.vue";
import UseCollapsedView from "~/composables/useCollapsedView";
import { useBetbook } from "~/store/betbook";
import { useSettings } from "~/store/settings";
import { IStreamConfig } from "~/types/api/streaming";
import { ISport } from "~/types/sports/sport";
import api from "~/utils/auth/api";
import { fetchStreamConfig, parseStreamingConfig } from "~/utils/streaming/streaming-util";
import SportId from "./[sport]/[sportId].vue";

interface Props {
	liveOnly?: boolean;
}

const props = defineProps<Props>();
const route = useRoute();
const store = useBetbook();
const settings = useSettings();
const isCollapsedView = UseCollapsedView();

const liveOnlyQuery = computed(() => props.liveOnly || Boolean(route.query.liveOnly));
const isEsports = computed(() => route.params.sport === "esports" || route.fullPath.includes("esports"));
const { data: sports, isLoading: isSportsLoading } = useQuery<{ sports: ISport[] }>({
	queryKey: ["sports", isEsports],
	queryFn: () =>
		api({
			url: isEsports.value ? "/cron/esports/{brand}/{locale}" : "/cron/sports/{brand}/{locale}",
			options: {
				method: "GET",
			},
		}),
	onSuccess: (data) => {
		store.sportsBonusAllowedSports = data.sports.filter((sport) => sport.isSportBonusAllowed);
	},
});

// Fetch streamable leagues and save in store
onMounted(() => {
	fetchStreamConfig();
});

const isTrackRacing = computed(() => {
	return ["greyhounds", "horse-racing"].includes(route.params.sportId as string);
});
</script>

<template>
	<SportsBookDownLanding
		:path="settings.sitemap?.find((x) => x.A.toLowerCase() === 'sportsbook-maintenance')?.P"
		v-if="settings.hasFeature('IsSplashPageEnabled')"
	/>
	<SportsLayout v-else>
		<NewsBox v-if="!isCollapsedView" />
		<DataFreeGuard>
			<Banner
				v-if="!isCollapsedView"
				:vertical="isTrackRacing ? 'horseracing' : 'sport'"
				slide-count="3"
				class="-mx-2 -mt-2 md:mx-0 md:mt-0"
			/>
		</DataFreeGuard>
		<div v-if="!isCollapsedView" class="block md:hidden">
			<BookABetWidget />
		</div>
		<SportGameList
			:is-esports="isEsports"
			:isLoading="isSportsLoading"
			:sports="sports?.sports"
			:liveOnly="liveOnlyQuery"
			v-if="!settings.simpleView && !isCollapsedView && !isTrackRacing"
		/>

		<RacingView v-if="isTrackRacing" />
		<div v-if="!isTrackRacing">
			<SportSimpleViewActions
				:is-esports="isEsports"
				:isLoading="isSportsLoading"
				:sports="sports?.sports"
				:liveOnly="liveOnlyQuery"
				v-if="settings.simpleView"
			/>
			<NuxtPage v-if="route.params.sport" :isEsports="isEsports" :sports="sports" :isSportsLoading="isSportsLoading" />
			<SimpleViewCombinedView
				v-else-if="!route.params.sport && settings.simpleView && !liveOnlyQuery"
				:isEsports="isEsports"
				:sports="sports"
				:isSportsLoading="isSportsLoading"
			/>
			<SportId v-else :isEsports="isEsports" :sports="sports" :isSportsLoading="isSportsLoading" :liveOnly="liveOnlyQuery" />
		</div>
	</SportsLayout>
</template>
